import {useCallback, useEffect, useRef, useState} from 'react';
import {channel} from 'redux-saga'
import {call, select, takeLatest} from 'redux-saga/effects';

import {useLoggedIn, useStoreFeed, useStoreProfile} from "../store";
import {actionEffects as actions} from "../store/actions";
import {selectFeedEntry, selectFeedIsLoaded, selectFeedLastPost} from "../store/selectors";
import {normalizeCommunityName} from "../store/communities";
import {useSagaEffect} from '../utils/sagas';
import {MINUTE_IN_MILLIS} from "../utils/time";
import {apiGet, apiPostJSON} from './client';
import {loadCommunities} from "./communities";
import {useActions} from "../app_context";

function* apiRequest(method, path, query) {
    if (method === 'POST') {
        return yield call(apiPostJSON, path, query);
    }
    const parts = [];
    for (const [key, val] of Object.entries(query)) {
        parts.push(`${key}=${encodeURIComponent(val)}`);
    }
    return yield call(apiGet, `${path}?${parts.join('&')}`);
}

function* loadCommunitiesForPosts(posts) {
    const communitySet = new Set();
    const communityMap = yield select(({ communityMap }) => communityMap);
    for (const { community } of posts) {
        if (!communityMap.has(community)) {
            communitySet.add(community);
        }
    }
    yield call(loadCommunities, Array.from(communitySet.values()));
}

function* loadFirstPage(key, method, path, query) {
    yield actions.feedLoading(key);
    try {
        const {posts, has_more_entries: more} = yield call(apiRequest, method, path, query);
        yield actions.feedLoaded(key, posts, typeof more === 'boolean' ? more : true);
        yield call(loadCommunitiesForPosts, posts);
    } catch {
        yield actions.feedTailLoadFailed(key);
    }
}

function* poll(key, method, path, query) {
    yield actions.feedLoading(key);
    try {
        const {posts} = yield call(apiRequest, method, path, query);
        yield actions.feedPolled(key, posts);
    } catch {
        yield actions.feedTailLoadFailed(key);
    }
}

function* loadNextPage(key, paginated, method, path, query) {
    if (paginated) {
        const {page} = yield select(selectFeedEntry(key));
        query.page = page + 1;
    } else {
        const lastPost = yield select(selectFeedLastPost(key));
        if (!lastPost) {
            return;
        }
        query.from = lastPost.uuid;
    }
    yield actions.feedLoading(key);
    try {
        const {posts, has_more_entries: more} = yield call(apiRequest, method, path, query);
        yield actions.feedTailLoaded(key, posts, typeof more === 'boolean' ? more : true);
        yield call(loadCommunitiesForPosts, posts);
    } catch {
        yield actions.feedTailLoadFailed(key);
    }
}

function* loadFeed(key, ch, paginated, method, path, query) {
    yield takeLatest(ch, function* ({ type }) {
        switch (type) {
        case 'loadNextPage':
            yield call(loadNextPage, key, paginated, method, path, query);
            break;
        case 'refresh':
            yield call(loadFirstPage, key, method, path, query);
            break;
        case 'poll':
            yield call(poll, key, method, path, query);
            break;
        default:
            throw new Error(type);
        }
    });
    yield takeLatest('userChanged', function* () {
        yield call(loadFirstPage, key, method, path, query);
    });
    const loaded = yield select(selectFeedIsLoaded(key));
    if (!loaded) {
        yield call(loadFirstPage, key, method, path, query);
    }
}

export const useFeed = (feedbackCh, key, paginated, signalRef, method, path, query, loadFromFresh = true, isRestorableFeed = true) => {
    const actionChRef = useRef(null);
    if (!actionChRef.current) {
        actionChRef.current = channel();
    }
    const actions = useActions();
    useSagaEffect(feedbackCh, useCallback(function* () {
        yield call(loadFeed, key, actionChRef.current, paginated, method, path, query);
    }, [key, method, path, JSON.stringify(query)]));
    const clearFeedData = useCallback((feedKey) => {
        actions.feedAdUnitsCleared(feedKey);
        actions.feedLoadReset(feedKey);
    }, []);
    const refresh = useCallback((feedKey) => {
        // clear any stored data for this feed
        clearFeedData(feedKey);
        actionChRef.current.put({ type: 'refresh' });
    }, [key]);
    const loadNextPage = useCallback(() => actionChRef.current.put({ type: 'loadNextPage' }), []);
    const poll = useCallback(() => actionChRef.current.put({ type: 'poll' }), []);
    const feed = useStoreFeed(key);
    const [loadedFresh, setLoadedFresh] = useState(false);
    useEffect(() => {
        if (!feed.loading) {
            if (feed.cleared && !loadedFresh) {
                refresh(key);
            } else if (loadFromFresh && !loadedFresh) {
                if (loadFromFresh) {
                    setLoadedFresh(true);
                }
                refresh(key);
                // Scroll to top and refresh feed if new/cleared.
                signalRef.current = 'scrollToTop';
            } else if (feed.loaded) {
                const now = Date.now();
                if (now > feed.timestamp + 15 * MINUTE_IN_MILLIS && !isRestorableFeed) {
                    signalRef.current = 'scrollToTop';
                    refresh();
                } else if (!feed.polled && now > feed.timestamp + 2 * MINUTE_IN_MILLIS) {
                    poll();
                }
            }
        }
    }, [feed.loading, feed.cleared, feed.loaded, feed.polled, feed.timestamp, loadedFresh]);

    return {
        ...feed,
        refresh: !feed.loading && refresh,
        loadNextPage: feed.more && !feed.loading && loadNextPage
    };
};

export const useAltPosts = (feedbackCh, feed, community, signalRef, key, loadFromFresh) => {
    community = normalizeCommunityName(community);
    const path = `/api/v2/post/${feed}.json`;
    return useFeed(feedbackCh, key, true, signalRef, 'GET', path, {community}, loadFromFresh);
};

export const useSearch = (feedbackCh, query, community, sort, signalRef, key, loadFromFresh) => {
    community = normalizeCommunityName(community);
    const path = `/api/v2/post/search.json`;
    return useFeed(feedbackCh, key, true, signalRef, 'GET', path, {query, community, sort}, loadFromFresh);
};

export const useFeedPosts = (feedbackCh, key, community, sortBy, sort, feedId, signalRef, trendingTopics, loadFromFresh) => {
    const loggedIn = useLoggedIn();
    const {subscribed, blocked_communities} = useStoreProfile();
    community = normalizeCommunityName(community);
    sortBy += "v2";
    const method = community === 'home' && !loggedIn ? 'POST' : 'GET';
    const path = `/api/v2/post/${sortBy}.json`;
    const query = {community};
    if (community === 'home' && !loggedIn) {
        query.communities = subscribed;
    }
    if (feedId && community === 'win') {
        query.feedId = feedId;
    }
    if ((sortBy === 'top' || sortBy === 'topv2') && sort) {
        query.sort = sort;
    }
    if (!feedId && community === 'win') {
        query.isTrendingTopics = true;
        query.trendingTopics = JSON.stringify(trendingTopics);
        query.blockedCommunities = JSON.stringify(blocked_communities || []);
    }
    return useFeed(feedbackCh, key, false, signalRef, method, path, query, loadFromFresh);
};

export const useProfilePosts = (feedbackCh, username, signalRef, key, loadFromFresh) => {
    const path = '/api/v2/post/profile.json';
    return useFeed(feedbackCh, key, true, signalRef, 'GET', path, {user: username}, loadFromFresh);
};